import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import TextField from '@mui/material/TextField';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import useAuth from '../../../../features/hooks/useAuth';
import { useSelector } from 'react-redux';
import { CommonButton } from '../../../../Styles/CommonStyles';
import { useGetUsersQuery } from '../../../../features/users/usersApiSlice';
import { useGetofficesQuery } from '../../../../features/offices/officesApiSlice';
import { useUpdateConsumableReqMutation } from '../../../../features/consumablerequest/consumableReqApiSlice';
import { useGetOfficeItemsQuery } from '../../../../features/officeitems/officeItemsApiSlice';
import { useGetconsummasterQuery } from '../../../../features/consummaster/consummasterApiSlice';
import { useGetpartnumberQuery } from '../../../../features/partnumber/partnumberApiSlice';
import { useGetModelNumbersQuery } from '../../../../features/modelnumber/modelNumberApiSlice';
import nameStringFn from '../../../../data/nameStringFn';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import EditIcon from '@mui/icons-material/Edit';
import Button from '@mui/material/Button';
import Autocomplete from '@mui/material/Autocomplete';
import ModifyConsumablesModal from './ModifyConsumablesModal';
import { useAlert } from '../../../ui/AlertProvider';

const StyledButton = styled(CommonButton)`
  padding: 8px 16px;
  min-width: 120px;
  margin-right: 10px;
`;

const ActionButton = styled(StyledButton)`
  background-color: #1976d2;
`;

const ReturnButton = styled(StyledButton)`
  background-color: #ff9800;
`;

const RejectButton = styled(StyledButton)`
  background-color: #f44336;
`;

const ForwardButton = styled(StyledButton)`
  background-color: #4caf50;
`;

const IssueButton = styled(StyledButton)`
  background-color: #ff9800;
`;

const FinalIssueButton = styled(StyledButton)`
  background-color: #2e7d32;
`;

const RemarkContainer = styled.div`
  margin-top: 15px;
  width: 100%;
`;

const PartialConsumablesModalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '600px',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: '8px'
};

const ModalTitle = styled.h2`
  margin-bottom: 20px;
  color: #1976d2;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  gap: 10px;
`;

const SelectedConsumablesContainer = styled.div`
  margin-top: 20px;
  width: 100%;
`;

const SelectedConsumablesTitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const SelectedConsumablesTitle = styled.h3`
  margin-bottom: 0;
  color: #1976d2;
`;

const StyledTableContainer = styled(TableContainer)`
  margin-bottom: 20px;
`;

const StyledTableHead = styled(TableHead)`
  background-color: #1976d2;
  
  & th {
    color: white;
    font-weight: bold;
  }
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 10px;
`;

const OptionCard = styled.div`
  width: 100%;
  padding: 16px;
  margin: 12px 0;
  border-radius: 8px;
  background-color: #f5f5f5;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  }
`;

const ForwardStoreCard = styled(OptionCard)`
  border-left: 5px solid #2196f3;
  
  &:hover {
    background-color: #e3f2fd;
  }
`;

const CloseRequestCard = styled(OptionCard)`
  border-left: 5px solid #f44336;
  
  &:hover {
    background-color: #ffebee;
  }
`;

const CardContent = styled.div`
  flex: 1;
`;

const CardTitle = styled.h4`
  margin: 0 0 8px 0;
  color: #333;
`;

const CardDescription = styled.p`
  margin: 0;
  color: #666;
  font-size: 0.9rem;
`;

const NotificationMessage = styled.div`
  margin: 20px 0;
  padding: 12px 16px;
  border-radius: 4px;
  font-weight: 500;
  display: flex;
  align-items: center;
  background-color: ${(props) => (props.isPartial ? '#ff9800' : '#4caf50')};
  color: white;
`;

const BoInboxFormCIR = ({ row, handleBack }) => {
  // Using separate state hooks instead of consolidated formState
  const [remark, setRemark] = useState('');
  const [openModifyModal, setOpenModifyModal] = useState(false);
  const [openPartialModal, setOpenPartialModal] = useState(false);
  const [selectedConsumables, setSelectedConsumables] = useState([]);
  const [isUpdatingReq, setIsUpdatingReq] = useState(false);
  
  const auth = useAuth();
  const user_current_office = useSelector((state) => state.office.off);
  const alert = useAlert();
  
  const { getSingleuser } = useGetUsersQuery('userslist', {
    selectFromResult: ({ data }) => ({
      getSingleuser: data?.entities
    }),
  });

  const { officedata } = useGetofficesQuery('offices', {
    selectFromResult: ({ data }) => ({
      officedata: data?.entities
    }),
  });

  // Fetch office items for current office to get available quantities
  const { officeItems } = useGetOfficeItemsQuery({ office_name: user_current_office }, {
    selectFromResult: ({ data }) => ({
      officeItems: data?.entities
    }),
  });

  // Master data queries for part number selection
  const { consumablemaster } = useGetconsummasterQuery('cosummaster', {
    selectFromResult: ({ data }) => ({
      consumablemaster: data?.entities
    }),
  });

  const { partnumber } = useGetpartnumberQuery('partno', {
    selectFromResult: ({ data }) => ({
      partnumber: data?.entities
    }),
  });

  const { modelnumber } = useGetModelNumbersQuery('modelnumber', {
    selectFromResult: ({ data }) => ({  
      modelnumber: data?.entities
    }),
  });

  // Memoize these calculations to prevent unnecessary recalculations
  const officeIds = useMemo(() => {
    if (!officedata) return { BO_id: "", UnitHead_id: "" };
    
    const userOffice = Object.values(officedata)?.find(office => office?.name === user_current_office);
    return {
      BO_id: userOffice?.Bo || "",
      UnitHead_id: userOffice?.unitHead || ""
    };
  }, [officedata, user_current_office]);
  
  // Destructure for easier access
  const { BO_id, UnitHead_id } = officeIds;

  const [updateConsumableReq] = useUpdateConsumableReqMutation();

  // Function to get available quantity for a particular partno - memoized
  const getAvailableQuantity = useCallback((partno) => {
    if (!officeItems || !partno) return 0;
    
    // Find the office inventory for current office
    const currentOfficeInventory = Object.values(officeItems).find(office => 
      office.office_name === user_current_office
    );
    
    if (!currentOfficeInventory?.consumable_details) return 0;
    
    // Find the consumable item by partno
    const consumableItem = currentOfficeInventory.consumable_details.find(item => 
      item.partno === partno
    );
    
    return consumableItem ? parseInt(consumableItem.quantity || 0, 10) : 0;
  }, [officeItems, user_current_office]);

  // Helper function to get filtered part numbers based on asset and consumable
  const filteredPartNumbers = useCallback((assetName, brand, consumable) => {
    if (!modelnumber || !partnumber) return [];
  
    const modelNos = Object.values(modelnumber || {})
      .filter(m => m.asset === assetName && m.brand === brand)
      .map(m => m.modelno);
  
    return Object.values(partnumber || {}).filter(pn =>
      pn.consumable === consumable &&
      (
        pn.assetModelNos.includes(assetName) || // Exact asset name match
        pn.assetModelNos.some(model => modelNos.includes(model)) || // Model number match
        pn.assetModelNos.includes(`All-${assetName}`) || // "All-Asset" match
        pn.assetModelNos.includes(`All-${assetName}-${brand}`) // "All-Asset-Brand" match
      )
    ).map(pn => pn.partno);
  }, [modelnumber, partnumber]);

  // Get available part numbers for a consumable - memoized
  // Excludes part numbers already selected in other rows to prevent duplicates
  const getPartNumberOptions = useCallback((consumable, asset, assetTag, currentIndex) => {
    if (!partnumber || !modelnumber) return [];
    
    // Get all possible part numbers for this consumable
    let availablePartNumbers = [];
    
    // If asset is "Not Applicable", return all part numbers for this consumable
    if (asset === "Not Applicable" || asset === null) {
      availablePartNumbers = Object.values(partnumber)
        .filter(pn => pn.consumable === consumable)
        .map(pn => pn.partno);
    } else {
      // If asset is specified, filter by asset and model
      availablePartNumbers = filteredPartNumbers(asset, assetTag?.value?.oem || assetTag?.oem, consumable);
    }
    
    // Filter out part numbers already selected in other rows
    const selectedPartNumbers = selectedConsumables
      .map((item, idx) => idx !== currentIndex ? item.partno : null)
      .filter(partno => partno !== null && partno !== undefined && partno !== '');
    
    return availablePartNumbers.filter(partno => !selectedPartNumbers.includes(partno));
  }, [partnumber, modelnumber, filteredPartNumbers, selectedConsumables]);

  // Initialize selectedConsumables with the latest requested consumables
  useEffect(() => {
    if (row?.consumables_requested?.length > 0) {
      const latestRequestedConsumables = row.consumables_requested[row.consumables_requested.length - 1].data;
      
      // Check if this is a return from Unit Head scenario
      const lastAction = row?.req_data?.[row.req_data.length - 1];
      const isReturnedFromUnitHead = lastAction?.sent_to_tag === 'userreq_uh_retrn_bo_mod_cir';
      const hasBoIssue = row?.bo_consum_issue?.length > 0;
      
      let mappedConsumables = [];
      
      // If returned from Unit Head and BO had previously issued consumables, restore that state
      if (isReturnedFromUnitHead && hasBoIssue) {
        const lastBoIssue = row.bo_consum_issue[row.bo_consum_issue.length - 1];
        
        // Create a map of original requests for grouping
        const requestMap = new Map();
        latestRequestedConsumables.forEach((item, index) => {
          const key = `${item.consumable}-${item.asset}-${item.assettag?.label || ''}`;
          if (!requestMap.has(key)) {
            requestMap.set(key, {
              groupId: `group-${index}`,
              originalQuantity: parseInt(item.quantity, 10),
              items: []
            });
          }
        });
        
        // Map BO issued consumables back with proper grouping
        mappedConsumables = lastBoIssue.data.map((issuedItem, index) => {
          const key = `${issuedItem.consumable}-${issuedItem.asset}-${issuedItem.assettag?.label || ''}`;
          const groupInfo = requestMap.get(key) || {
            groupId: `group-${index}`,
            originalQuantity: parseInt(issuedItem.quantity, 10)
          };
          
          return {
            ...issuedItem,
            groupId: groupInfo.groupId,
            isSplit: false, // Will be determined if multiple items share same group
            originalQuantity: groupInfo.originalQuantity,
            quantity: groupInfo.originalQuantity, // Display the original requested quantity
            partno: issuedItem.partno || null,
            availableQuantity: groupInfo.originalQuantity,
            selectedQuantity: parseInt(issuedItem.quantity, 10), // This is the issued quantity
            allocatedQuantity: issuedItem.originalRequestedQty || parseInt(issuedItem.quantity, 10) // Restore the allocated quantity
          };
        });
        
        // Mark split items (multiple items with same groupId)
        const groupCounts = mappedConsumables.reduce((acc, item) => {
          acc[item.groupId] = (acc[item.groupId] || 0) + 1;
          return acc;
        }, {});
        
        mappedConsumables = mappedConsumables.map((item, idx) => ({
          ...item,
          isSplit: idx > 0 && groupCounts[item.groupId] > 1
        }));
      } else {
        // Fresh issue - Map from requested consumables
        mappedConsumables = latestRequestedConsumables.map((item, index) => ({
          ...item,
          groupId: `group-${index}`, // Add groupId to track original requests and their splits
          isSplit: false, // Track if this is a split item
          originalQuantity: parseInt(item.quantity, 10), // Store original requested quantity
          partno: item.partno || null, // Ensure partno is properly set
          availableQuantity: parseInt(item.quantity, 10),
          selectedQuantity: item.partno ? parseInt(item.quantity, 10) : 0, // Use selectedQuantity instead of issuedQuantity
          modReqQuantity:parseInt(item.quantity, 10), // New field to track modified requested quantity in modal
          allocatedQuantity: undefined // Will be set when BO confirms quantities in the modal
        }));
      }
      
      setSelectedConsumables(mappedConsumables);
    }
  }, [row]);

  // Function to check if partial issue is applicable - memoized to prevent recalculation
  const isPartialIssue = useCallback(() => {
    if (!selectedConsumables.length) return false;
    
    // Group by groupId and check if any group's total selected is less than original
    const groupTotals = selectedConsumables.reduce((acc, item) => {
      if (!acc[item.groupId]) {
        acc[item.groupId] = {
          originalQuantity: parseInt(item.originalQuantity || item.quantity, 10),
          selectedTotal: 0
        };
      }
      acc[item.groupId].selectedTotal += parseInt(item.selectedQuantity, 10) || 0;
      return acc;
    }, {});
    
    // Check if any group has selected less than original
    return Object.values(groupTotals).some(group => 
      group.selectedTotal < group.originalQuantity
    );
  }, [selectedConsumables]);

  // Function to check if any part numbers are selected
  const hasAnyPartNumberSelected = useCallback(() => {
    return selectedConsumables.some(item => item.partno !== null && item.partno !== undefined && item.partno !== '');
  }, [selectedConsumables]);

  // Function to check if all selected part numbers have available quantity
  const allSelectedHaveQuantity = useCallback(() => {
    const itemsWithPartNumber = selectedConsumables.filter(item => 
      item.partno !== null && item.partno !== undefined && item.partno !== ''
    );
    
    // If no part numbers selected, return false
    if (itemsWithPartNumber.length === 0) return false;
    
    // Check if all selected items have at least 1 available quantity
    return itemsWithPartNumber.every(item => getAvailableQuantity(item.partno) > 0);
  }, [selectedConsumables, getAvailableQuantity]);

  // Get list of selected items with 0 quantity for warning message
  const getItemsWithZeroQuantity = useCallback(() => {
    return selectedConsumables.filter(item => 
      item.partno && getAvailableQuantity(item.partno) === 0
    );
  }, [selectedConsumables, getAvailableQuantity]);

  // Handler for opening the modify consumables modal
  const handleModifyConsumables = useCallback(() => {
    setOpenModifyModal(true);
  }, []);

  // Handler for closing the modify consumables modal
  const handleCloseModifyModal = useCallback(() => {
    setOpenModifyModal(false);
  }, []);

  // Handler for updating the issued quantity
  const handleQuantityChange = useCallback((index, value) => {
    const newValue = parseInt(value, 10) || 0;
    const item = selectedConsumables[index];
    
    // Calculate current group total excluding the current item
    const currentGroupTotal = selectedConsumables
      .filter((c, i) => c.groupId === item.groupId && i !== index)
      .reduce((sum, c) => sum + (parseInt(c.selectedQuantity, 10) || 0), 0);
    
    // Maximum value is the lower of: original quantity minus other items in group
    const maxByGroup = item.originalQuantity - currentGroupTotal;
    
    // Ensure the value doesn't go below 0 or exceed the group limit
    const validValue = Math.min(Math.max(0, newValue), maxByGroup);

    const baseValueIndex = selectedConsumables.findIndex((c, i) => c.groupId === item.groupId && !c.isSplit);
    
    const updatedConsumables = [...selectedConsumables];
    updatedConsumables[index] = {
      ...updatedConsumables[index],
      selectedQuantity: validValue,
      ...(baseValueIndex !== index && { modReqQuantity: validValue })
    };

    const totalModReqQuantity = updatedConsumables
      .filter(item => item.isSplit)
      .reduce((sum, item) => sum + (item.modReqQuantity || 0), 0);

    if(baseValueIndex !== index) {
      console.log(updatedConsumables[baseValueIndex].originalQuantity)
      console.log(totalModReqQuantity)
        updatedConsumables[baseValueIndex] = {
        ...updatedConsumables[baseValueIndex],
        modReqQuantity: (updatedConsumables[baseValueIndex].originalQuantity - totalModReqQuantity)
      };
    }
    console.log(index);
    console.log(baseValueIndex);
    console.log(updatedConsumables);
    setSelectedConsumables(updatedConsumables);
  }, [selectedConsumables]);

  // Handler for part number selection
  const handlePartNumberChange = useCallback((index, newPartNumber) => {
    const updatedConsumables = [...selectedConsumables];
    const item = updatedConsumables[index];
    
    // Only auto-fill quantity if this is the first time selecting a part number (partno was null/empty)
    let newSelectedQuantity = item.selectedQuantity;
    
    // if (!item.partno && newPartNumber) {
    //   // This is the first time selecting a part number - auto-fill with remaining quantity
    //   const currentGroupTotal = selectedConsumables
    //     .filter((c, i) => c.groupId === item.groupId && i !== index)
    //     .reduce((sum, c) => sum + (parseInt(c.selectedQuantity, 10) || 0), 0);
      
    //   const remainingQty = item.originalQuantity - currentGroupTotal;
    //   newSelectedQuantity = remainingQty;
    // } else
      
    if (!newPartNumber) {
      // Part number is being cleared - reset quantity to 0
      newSelectedQuantity = 0;
    }
    // If changing from one part number to another, keep the existing selectedQuantity
    
    updatedConsumables[index] = {
      ...updatedConsumables[index],
      partno: newPartNumber,
      selectedQuantity: newSelectedQuantity
    };
    
    setSelectedConsumables(updatedConsumables);
  }, [selectedConsumables]);

  // Handler for confirming modified quantities
  const handleConfirmModification = useCallback(() => {
    // Validate that total quantities per group don't exceed requested
    const groupValidation = {};
    
    selectedConsumables.forEach(item => {
      if (!groupValidation[item.groupId]) {
        groupValidation[item.groupId] = {
          originalQuantity: item.originalQuantity,
          totalSelected: 0
        };
      }
      groupValidation[item.groupId].totalSelected += parseInt(item.selectedQuantity, 10) || 0;
    });
    
    // Check if any group exceeds the original quantity
    for (const groupId in groupValidation) {
      const { originalQuantity, totalSelected } = groupValidation[groupId];
      if (totalSelected > originalQuantity) {
        alert.showWarning(`Total selected quantity (${totalSelected}) exceeds requested quantity (${originalQuantity}) for one or more items. Please adjust the quantities.`);
        return;
      }
    }
    
    // Detect if this is a reallocation or a partial issue scenario
    // If the sum of selectedQuantity for each group equals originalQuantity, it's a reallocation
    const groupTotals = selectedConsumables.reduce((acc, item) => {
      if (!acc[item.groupId]) {
        acc[item.groupId] = {
          originalQuantity: item.originalQuantity,
          selectedTotal: 0
        };
      }
      acc[item.groupId].selectedTotal += parseInt(item.selectedQuantity, 10) || 0;
      return acc;
    }, {});
    
    // Update allocatedQuantity based on scenario
    const updatedConsumables = selectedConsumables.map(item => {
      const groupTotal = groupTotals[item.groupId];
      const isReallocation = groupTotal.selectedTotal === groupTotal.originalQuantity;
      
      return {
        ...item,
        allocatedQuantity: (item.allocatedQuantity !== undefined && !isReallocation)
          ? item.allocatedQuantity // Keep existing allocation if it's a partial issue scenario
          : parseInt(item.selectedQuantity, 10) // Set/update allocation if it's first time or reallocation
      };
    });
    
    setSelectedConsumables(updatedConsumables);
    setOpenModifyModal(false);
  }, [selectedConsumables, alert]);

  // Handler for splitting a consumable row into multiple rows
  const handleSplitRow = useCallback((index) => {
    const itemToSplit = selectedConsumables[index];
    
    // Create a new split row with the same properties but reset quantities
    const newSplitRow = {
      ...itemToSplit,
      isSplit: true,
      partno: null, // Reset part number for new split
      selectedQuantity: 0, // Reset selected quantity
      modReqQuantity: 0,
      availableQuantity: itemToSplit.originalQuantity, // Use original quantity as max
      allocatedQuantity: undefined // Will be set when BO confirms quantities
    };
    
    // Insert the new split row after the current item
    const updatedConsumables = [
      ...selectedConsumables.slice(0, index + 1),
      newSplitRow,
      ...selectedConsumables.slice(index + 1)
    ];
    
    setSelectedConsumables(updatedConsumables);
  }, [selectedConsumables]);

  // Handler for removing a split row
  const handleRemoveSplitRow = useCallback((index) => {
    const itemToRemove = selectedConsumables[index];
    
    // Only allow removal of split items, not original items
    if (!itemToRemove.isSplit) {
      alert.showWarning("Cannot remove original request item. You can only remove split rows.");
      return;
    }
    
    // Remove the item at the specified index
    const updatedConsumables = selectedConsumables.filter((_, i) => i !== index);
    setSelectedConsumables(updatedConsumables);
  }, [selectedConsumables, alert]);

  // Get total selected quantity for a group
  const getGroupTotalQuantity = useCallback((groupId) => {
    return selectedConsumables
      .filter(item => item.groupId === groupId)
      .reduce((sum, item) => sum + (parseInt(item.selectedQuantity, 10) || 0), 0);
  }, [selectedConsumables]);

  // Handler for the issue all consumables button
  const handleFinalIssue = useCallback(() => {
    // Check if there's any partial issue
    if (isPartialIssue()) {
      // Open modal for partial consumables options
      setOpenPartialModal(true);
    } else {
      // Regular full issuance
      handleConsumablesIssue('full');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPartialIssue]);

  // Handler for closing the partial consumables modal
  const handleClosePartialModal = useCallback(() => {
    setOpenPartialModal(false);
  }, []);

  // Helper for creating sent to data - reduces duplication
  const createSentToData = useCallback((sent_to_tag, action, sent_to_id, sent_to_office, sent_to_designation, additionalData = {}) => {
    return {
      sent_to_tag,
      Text: remark,
      sent_by: { 
        id: auth?.id, 
        name: getSingleuser?.[auth?.id]?.name, 
        office: user_current_office, 
        designation: auth?.designation?.[user_current_office] 
      },
      sent_to: { 
        id: sent_to_id, 
        name: getSingleuser?.[sent_to_id]?.name, 
        office: sent_to_office || user_current_office, 
        designation: sent_to_designation || getSingleuser?.[sent_to_id]?.designation?.[user_current_office] 
      },
      action,
      curr_sent_to_string: `${nameStringFn(getSingleuser?.[sent_to_id])}-${sent_to_office || user_current_office}-${sent_to_designation || getSingleuser?.[sent_to_id]?.designation?.[user_current_office] || 'Unknown'}`,
      time_stamp: new Date(),
      ...additionalData
    };
  }, [auth, getSingleuser, user_current_office, remark]);

  // Unified handler for consumable issuance with different types
  const handleConsumablesIssue = useCallback(async (issueType) => {
    if (!remark.trim()) {
      alert.showWarning("Please add a remark before proceeding.");
      return;
    }
    
    let sent_to_tag, action, successMessage, additionalData = {};
    
    switch(issueType) {
      case 'full':
        sent_to_tag = "boreq_issueallclose_forapprv";
        action = "All requested consumables have been issued and sent to Unit Head for approval.";
        successMessage = "All requested consumables have been issued and sent to Unit Head for approval.";
        break;
      case 'partialForward':
        sent_to_tag = "boreq_parissue_forapprv";
        action = "Partial consumables issued and forwarded to store";
        successMessage = "Partial consumables issued and sent to Unit Head for approval before forwarding to store.";
        break;
      case 'partialClose':
        sent_to_tag = "boreq_parissueclse_forapprv";
        action = "Partial consumables issued and request closed";
        successMessage = "Partial consumables issued and sent to Unit Head for approval before closing the request.";
        break;
      default:
        alert.showError("Invalid issue type");
        return;
    }
    
    // Prepare consumables issue data - only include items that are actually being issued
    const issuedConsumables = selectedConsumables.filter(item => 
      item.partno && parseInt(item.selectedQuantity, 10) > 0
    );
    
    // Debug logging
    console.log('Validation Debug:', {
      selectedConsumables,
      issuedConsumables,
      validationCheck: selectedConsumables.map(item => ({
        consumable: item.consumable,
        partno: item.partno,
        selectedQuantity: item.selectedQuantity,
        hasPartno: !!item.partno,
        hasQuantity: parseInt(item.selectedQuantity, 10) > 0,
        passes: item.partno && parseInt(item.selectedQuantity, 10) > 0
      }))
    });
    
    // Validate that we have items to issue
    if (issuedConsumables.length === 0) {
      alert.showWarning("Please select part numbers and set issue quantities for at least one item.");
      return;
    }
    
    const consumIssueData = {
      time_stamp: new Date(),
      status: "draft", // Draft status when BO creates issue - becomes 'final' only when user accepts
      data: issuedConsumables.map(item => ({
        consumable: item.consumable,
        partno: item.partno,
        quantity: parseInt(item.selectedQuantity, 10), // Use selectedQuantity as the actual issued quantity
        originalRequestedQty: item.allocatedQuantity || parseInt(item.selectedQuantity, 10), // The allocated quantity for this split (e.g., 6, 1, 1), fallback to selectedQuantity if not set
        modReqQuantity: item.modReqQuantity,
        asset: item.asset,
        assettag: item.assettag
      }))
    };
    
    // Add BO consumable issue data to additionalData
    additionalData = {
      bo_partial_issue: issueType === 'full' ? false : true,
      bo_consum_issue: [consumIssueData]
    };
    
    // Debug logging
    console.log('BO Issue Data:', {
      issueType,
      issueStatus: 'draft', // BO creates draft issue - user will finalize
      selectedConsumables,
      issuedConsumables,
      consumIssueData,
      additionalData
    });
    
    // Create sent to data using the helper function
    const senttoData = createSentToData(
      sent_to_tag,
      action,
      UnitHead_id,
      user_current_office,
      getSingleuser?.[UnitHead_id]?.designation?.[user_current_office],
      additionalData
    );
    
    const body = { id: row?._id, senttoData };

    try {
      setIsUpdatingReq(true);
      await updateConsumableReq(body).unwrap();
      alert.showSuccess(successMessage, "Success", () => {
        // Close modal for partial cases
        if (issueType === 'partialForward' || issueType === 'partialClose') {
          setOpenPartialModal(false);
        }
        
        handleBack();
      });
    } catch (error) {
      console.error("Error issuing consumables:", error);
      alert.showError("There was an error issuing the consumables. Please try again.");
    } finally {
      setIsUpdatingReq(false);
    }
  }, [remark, selectedConsumables, UnitHead_id, user_current_office, getSingleuser, row, updateConsumableReq, createSentToData, handleBack, alert]);

  // Handler for forwarding request directly to store via Unit Head
  const handleForwardToUnitHead = useCallback(async () => {
    if (!remark.trim()) {
      alert.showWarning("Please add a remark before proceeding.");
      return;
    }
    
    const senttoData = createSentToData(
      "userreq_fwd_bo_to_uh_store_cir",
      "Request forwarded to Unit Head for store approval.",
      UnitHead_id,
      user_current_office,
      getSingleuser?.[UnitHead_id]?.designation?.[user_current_office]
    );
    
    const body = { id: row?._id, senttoData };
    
    try {
      setIsUpdatingReq(true);
      await updateConsumableReq(body).unwrap();
      alert.showSuccess("Request forwarded to Unit Head for store approval.", "Success", handleBack);
    } catch (error) {
      console.error("Error forwarding request:", error);
      alert.showError("There was an error forwarding the request. Please try again.");
    } finally {
      setIsUpdatingReq(false);
    }
  }, [remark, row, UnitHead_id, user_current_office, getSingleuser, createSentToData, updateConsumableReq, handleBack, alert]);

  // Handler for returning request to user for modification
  const handleReturnBack = useCallback(async () => {
    if (!remark.trim()) {
      alert.showWarning("Please provide a remark explaining what needs to be modified.");
      return;
    }

    const senttoData = createSentToData(
      "bo_returnto_user_cir",
      "Request returned to User for modification",
      row?.req_creator_id,
      row?.req_data[0]?.sent_by?.office,
      row?.req_data[0]?.sent_by?.designation
    );
    
    const body = { id: row?._id, senttoData };
    
    try {
      setIsUpdatingReq(true);
      await updateConsumableReq(body).unwrap();
      alert.showSuccess("Request returned to User for modification.", "Success", handleBack);
    } catch (error) {
      console.error("Error returning request:", error);
      alert.showError("There was an error returning the request. Please try again.");
    } finally {
      setIsUpdatingReq(false);
    }
  }, [remark, row, createSentToData, updateConsumableReq, handleBack, alert]);

  // Handler for rejecting request
  const handleRejectRequest = useCallback(async () => {
    if (!remark.trim()) {
      alert.showWarning("Please provide a reason for rejecting the request.");
      return;
    }

    const senttoData = createSentToData(
      "boreq_rejct_closed",
      "Request rejected",
      row?.req_creator_id,
      row?.req_data[0]?.sent_by?.office,
      row?.req_data[0]?.sent_by?.designation,
      { curr_status: "Rejected" }
    );
    
    const body = { id: row?._id, senttoData };
    
    try {
      setIsUpdatingReq(true);
      await updateConsumableReq(body).unwrap();
      alert.showSuccess("Request rejected successfully", "Request Rejected", handleBack);
    } catch (error) {
      console.error("Error rejecting request:", error);
      alert.showError("There was an error rejecting the request. Please try again.");
    } finally {
      setIsUpdatingReq(false);
    }
  }, [remark, row, createSentToData, updateConsumableReq, handleBack, alert]);

  // Remark change handler
  const handleRemarkChange = useCallback((e) => {
    setRemark(e.target.value);
  }, []);
console.log(selectedConsumables)
  // Memoize the TableRow rendering function
  const renderTableRows = useMemo(() => {
    return selectedConsumables.map((item, index) => (
      <TableRow key={`consumable-${index}`}>
        <TableCell>{index + 1}</TableCell>
        <TableCell>{item.consumable}</TableCell>
        <TableCell>{item.asset || 'Not Applicable'}</TableCell>
        <TableCell>{item.assettag?.label || 'Not Applicable'}</TableCell>
        <TableCell>
          <Autocomplete
            size="small"
            options={getPartNumberOptions(item.consumable, item.asset, item.assettag, index)}
            value={item.partno || null}
            onChange={(event, newValue) => handlePartNumberChange(index, newValue)}
            renderInput={(params) => <TextField {...params} label="Select Part No." />}
            sx={{ minWidth: 150 }}
          />
        </TableCell>
        <TableCell>{item.partno ? getAvailableQuantity(item.partno) : '-'}</TableCell>
        <TableCell>{item?.modReqQuantity}</TableCell>
        <TableCell>{item.selectedQuantity}</TableCell>
      </TableRow>
    ));
  }, [selectedConsumables, getAvailableQuantity, getPartNumberOptions, handlePartNumberChange]);

  return (
    <>
      <RemarkContainer>
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Add Remark"
          variant="outlined"
          value={remark}
          onChange={handleRemarkChange}
        />
      </RemarkContainer>

      {/* Display selected consumables in a table */}
      <SelectedConsumablesContainer>
        <SelectedConsumablesTitleContainer>
          <SelectedConsumablesTitle>Requested Consumables</SelectedConsumablesTitle>
          <ActionButtonsContainer>
            <Tooltip title="Modify quantities">
              <IconButton color="primary" onClick={handleModifyConsumables}>
                <EditIcon />
              </IconButton>
            </Tooltip>
          </ActionButtonsContainer>
        </SelectedConsumablesTitleContainer>

        <StyledTableContainer component={Paper}>
          <Table>
            <StyledTableHead>
              <TableRow>
                <TableCell>Sl.No.</TableCell>
                <TableCell>Consumable</TableCell>
                <TableCell>Asset</TableCell>
                <TableCell>Asset Tag</TableCell>
                <TableCell>Part No.</TableCell>
                <TableCell>Office Available Qty</TableCell>
                <TableCell>Requested Qty</TableCell>
                <TableCell>Issue Qty</TableCell>
              </TableRow>
            </StyledTableHead>
            <TableBody>
              {renderTableRows}
            </TableBody>
          </Table>
        </StyledTableContainer>

        {/* Warning message when selected items have 0 quantity */}
        {hasAnyPartNumberSelected() && !allSelectedHaveQuantity() && (
          <div style={{ 
            marginTop: '10px', 
            padding: '12px 16px', 
            color: '#d32f2f', 
            fontWeight: '500',
            backgroundColor: 'transparent'
          }}>
            ⚠️ Cannot issue consumables: The following items have 0 available quantity in office - 
            {getItemsWithZeroQuantity().map((item, idx) => (
              <span key={idx}>
                {idx > 0 && ', '}
                <strong> {item.consumable} (Part No: {item.partno})</strong>
              </span>
            ))}
          </div>
        )}

      </SelectedConsumablesContainer>

      {/* Rearranged button order as requested */}
      <div style={{ marginTop: '20px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
        {/* Show Forward button only when NO part numbers are selected */}
        {!hasAnyPartNumberSelected() && (
          <ForwardButton onClick={handleForwardToUnitHead} disabled={isUpdatingReq}>
            FORWARD AS IT IS TO UNIT HEAD FOR STORE
          </ForwardButton>
        )}

        {/* Show Issue buttons only when at least one part number is selected AND all have quantity */}
        {hasAnyPartNumberSelected() && allSelectedHaveQuantity() && (
          <IssueButton onClick={handleFinalIssue} disabled={isUpdatingReq}>
            {isPartialIssue() ? "ISSUE PARTIAL CONSUMABLES" : "ISSUE ALL CONSUMABLES"}
          </IssueButton>
        )}

        <ReturnButton onClick={handleReturnBack} disabled={isUpdatingReq}>
          RETURN TO USER FOR MODIFICATION
        </ReturnButton>

        <RejectButton onClick={handleRejectRequest} disabled={isUpdatingReq}>
          REJECT REQUEST
        </RejectButton>
      </div>

      {/* Modal for Modifying Consumables */}
      <ModifyConsumablesModal
        open={openModifyModal}
        onClose={handleCloseModifyModal}
        selectedConsumables={selectedConsumables}
        onQuantityChange={handleQuantityChange}
        onPartNumberChange={handlePartNumberChange}
        onSplitRow={handleSplitRow}
        onRemoveSplitRow={handleRemoveSplitRow}
        onConfirm={handleConfirmModification}
        getAvailableQuantity={getAvailableQuantity}
        getPartNumberOptions={getPartNumberOptions}
        getGroupTotalQuantity={getGroupTotalQuantity}
      />

      {/* Modal for Partial Consumables Options - Improved UI */}
      <Modal
        open={openPartialModal}
        onClose={handleClosePartialModal}
        aria-labelledby="partial-consumables-modal"
      >
        <Box sx={PartialConsumablesModalStyle}>
          <ModalTitle>Partial Consumables Issue Options</ModalTitle>

          <div style={{ marginBottom: '20px' }}>
            <p>You are about to issue fewer consumables than requested. Please select what you'd like to do next:</p>
          </div>

          <ForwardStoreCard onClick={() => handleConsumablesIssue('partialForward')}>
            <CardContent>
              <CardTitle>Forward Remaining to Store</CardTitle>
              <CardDescription>Issue the selected consumables and forward the request to Unit Head for store approval for remaining items</CardDescription>
            </CardContent>
          </ForwardStoreCard>

          <CloseRequestCard onClick={() => handleConsumablesIssue('partialClose')}>
            <CardContent>
              <CardTitle>Close Request</CardTitle>
              <CardDescription>Issue only the selected consumables and close the request completely</CardDescription>
            </CardContent>
          </CloseRequestCard>

          <ModalFooter>
            <Button variant="outlined" onClick={handleClosePartialModal}>Cancel</Button>
          </ModalFooter>
        </Box>
      </Modal>
    </>
  );
};

export default React.memo(BoInboxFormCIR); 